import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import {
  fetchAdminEmbeddingConfigs,
  createAdminEmbeddingConfig,
  updateAdminEmbeddingConfig,
  deleteAdminEmbeddingConfig,
  activateAdminEmbeddingConfig,
} from '../../api/admin';
import AdminDataTable, { Column } from '../../components/admin/AdminDataTable';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminConfirmModal from '../../components/admin/AdminConfirmModal';
import { FiPlus, FiEdit2, FiTrash2, FiZap } from 'react-icons/fi';

interface EmbeddingConfig {
  id: number;
  provider_name: string;
  display_name: string;
  endpoint_url: string;
  api_key: string | null;
  model_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PROVIDER_OPTIONS = ['ollama', 'huggingface_mxbai', 'voyage_finance', 'openai'];

const EMPTY_FORM = {
  provider_name: 'ollama',
  display_name: '',
  endpoint_url: '',
  api_key: '',
  model_name: '',
  is_active: false,
};

const PAGE_SIZE = 20;

const AdminEmbeddingConfig: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);

  const [configs, setConfigs] = useState<EmbeddingConfig[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_FORM });
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editConfig, setEditConfig] = useState<EmbeddingConfig | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminEmbeddingConfigs();
      const all: EmbeddingConfig[] = res.data.results || res.data.configs || res.data || [];
      setTotal(all.length);
      const start = (page - 1) * PAGE_SIZE;
      setConfigs(all.slice(start, start + PAGE_SIZE));
    } catch (err) {
      console.error('Failed to load embedding configs', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createForm.provider_name || !createForm.display_name || !createForm.endpoint_url || !createForm.model_name) {
      alert('Provider, display name, endpoint URL, and model name are required.');
      return;
    }
    setCreating(true);
    try {
      await createAdminEmbeddingConfig({
        ...createForm,
        api_key: createForm.api_key || null,
      });
      setShowCreate(false);
      setCreateForm({ ...EMPTY_FORM });
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to create config');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!editConfig) return;
    setSaving(true);
    try {
      await updateAdminEmbeddingConfig(editConfig.id, {
        ...editForm,
        api_key: editForm.api_key || null,
      });
      setEditConfig(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to update config');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    try {
      await deleteAdminEmbeddingConfig(deletingId);
      setDeletingId(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete config');
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await activateAdminEmbeddingConfig(id);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to activate config');
    }
  };

  const openEdit = (cfg: EmbeddingConfig) => {
    setEditConfig(cfg);
    setEditForm({
      provider_name: cfg.provider_name,
      display_name: cfg.display_name,
      endpoint_url: cfg.endpoint_url,
      api_key: '',
      model_name: cfg.model_name,
      is_active: cfg.is_active,
    });
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: colors.inputBackground,
    border: `1px solid ${colors.borderColor}`,
    color: colors.textColor,
  };

  const columns: Column<EmbeddingConfig>[] = [
    { key: 'display_name', header: 'Display Name', width: '140px' },
    {
      key: 'provider_name', header: 'Provider', width: '130px',
      render: (r) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={{
            backgroundColor: `${colors.primary}20`,
            color: colors.primary,
          }}
        >
          {r.provider_name}
        </span>
      ),
    },
    {
      key: 'model_name', header: 'Model', width: '180px',
      render: (r) => (
        <span className="text-xs font-mono" style={{ color: colors.grayScale500 }}>{r.model_name}</span>
      ),
    },
    {
      key: 'endpoint_url', header: 'Endpoint', width: '200px',
      render: (r) => (
        <span className="text-xs font-mono truncate block" style={{ color: colors.grayScale500, maxWidth: '200px' }}>{r.endpoint_url}</span>
      ),
    },
    {
      key: 'is_active', header: 'Active', width: '70px',
      render: (r) => (
        <span style={{ color: r.is_active ? colors.green : colors.alertColor, fontWeight: r.is_active ? 600 : 400 }}>
          {r.is_active ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'actions', header: 'Actions', width: '140px',
      render: (r) => (
        <div className="flex gap-2">
          {!r.is_active && (
            <button
              onClick={(e) => { e.stopPropagation(); handleActivate(r.id); }}
              className="p-1.5 rounded-lg transition-colors"
              style={{ backgroundColor: `${colors.green}15` }}
              title="Activate"
            >
              {React.createElement(FiZap as any, { size: 14, color: colors.green })}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); openEdit(r); }}
            className="p-1.5 rounded-lg transition-colors"
            style={{ backgroundColor: `${colors.primary}15` }}
            title="Edit"
          >
            {React.createElement(FiEdit2 as any, { size: 14, color: colors.primary })}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeletingId(r.id); }}
            className="p-1.5 rounded-lg transition-colors"
            style={{ backgroundColor: `${colors.alertColor}15` }}
            title="Delete"
          >
            {React.createElement(FiTrash2 as any, { size: 14, color: colors.alertColor })}
          </button>
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const renderFormFields = (
    form: typeof EMPTY_FORM,
    setForm: React.Dispatch<React.SetStateAction<typeof EMPTY_FORM>>,
  ) => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Display Name *</label>
          <input
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            value={form.display_name}
            onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
            placeholder="e.g. Ollama (self-hosted)"
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Provider *</label>
          <select
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            value={form.provider_name}
            onChange={(e) => setForm((p) => ({ ...p, provider_name: e.target.value }))}
          >
            {PROVIDER_OPTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Model Name *</label>
        <input
          className="w-full px-3 py-2 rounded-xl text-sm outline-none"
          style={inputStyle}
          value={form.model_name}
          onChange={(e) => setForm((p) => ({ ...p, model_name: e.target.value }))}
          placeholder="e.g. mxbai-embed-large"
        />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Endpoint URL *</label>
        <input
          className="w-full px-3 py-2 rounded-xl text-sm outline-none"
          style={inputStyle}
          value={form.endpoint_url}
          onChange={(e) => setForm((p) => ({ ...p, endpoint_url: e.target.value }))}
          placeholder="e.g. http://127.0.0.1:11434/api/embed"
        />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>API Key</label>
        <input
          className="w-full px-3 py-2 rounded-xl text-sm outline-none"
          style={inputStyle}
          type="password"
          value={form.api_key}
          onChange={(e) => setForm((p) => ({ ...p, api_key: e.target.value }))}
          placeholder="Leave blank if self-hosted"
        />
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.textColor }}>Embedding Configurations</h1>
          <p className="text-sm mt-1" style={{ color: colors.grayScale500 }}>
            Manage embedding providers for vector search. Only one config can be active at a time.
          </p>
        </div>
        <button
          onClick={() => { setCreateForm({ ...EMPTY_FORM }); setShowCreate(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white"
          style={{ backgroundColor: colors.primary }}
        >
          {React.createElement(FiPlus as any, { size: 16 })}
          Add Config
        </button>
      </div>

      {/* Table */}
      <AdminDataTable columns={columns} data={configs} loading={loading} emptyMessage="No embedding configs found" />
      <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div
            className="rounded-2xl p-6 w-full max-w-lg"
            style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>Add Embedding Config</h3>
            {renderFormFields(createForm, setCreateForm)}
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editConfig && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div
            className="rounded-2xl p-6 w-full max-w-lg"
            style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>
              Edit: {editConfig.display_name}
            </h3>
            {renderFormFields(editForm, setEditForm)}
            <p className="text-xs mt-2" style={{ color: colors.grayScale500 }}>
              Leave API key blank to keep existing value.
            </p>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setEditConfig(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deletingId !== null && (
        <AdminConfirmModal
          title="Delete Embedding Config"
          message="Are you sure you want to delete this embedding configuration? This cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
          destructive
        />
      )}
    </div>
  );
};

export default AdminEmbeddingConfig;
